import { AlertTriangle, CheckCircle2, Clock, PauseCircle } from 'lucide-react';

export type SlaState = 'OK' | 'WARNING' | 'BREACHED' | 'PAUSED' | null;

/**
 * One place that decides what an SLA state looks like.
 *
 * Colour alone never carries the meaning. Roughly one man in twelve cannot
 * separate the amber from the red reliably, and this is exactly the pair the
 * whole scheme leans on — so every state also carries a word and an icon. The
 * colour makes a breach findable at a glance across a full board; the label is
 * what actually says which it is.
 */
const STYLE: Record<
  Exclude<SlaState, null>,
  { chip: string; bar: string; edge: string; icon: typeof Clock; label: string }
> = {
  OK: {
    chip: 'state-resolved border-state-resolved-fg/20',
    bar: 'bg-state-resolved-fg',
    edge: 'border-l-secondary-400',
    icon: CheckCircle2,
    label: 'On time',
  },
  WARNING: {
    chip: 'state-progress border-state-progress-fg/20',
    bar: 'bg-state-progress-fg',
    edge: 'border-l-accent-400',
    icon: Clock,
    label: 'Due soon',
  },
  BREACHED: {
    chip: 'state-overdue border-state-overdue-fg/20',
    bar: 'bg-state-overdue-fg',
    edge: 'border-l-danger-500',
    icon: AlertTriangle,
    label: 'Breached',
  },
  PAUSED: {
    chip: 'state-new border-state-new-fg/20',
    bar: 'bg-muted-foreground/40',
    edge: 'border-l-muted-foreground/40',
    icon: PauseCircle,
    label: 'Paused',
  },
};

/** The left edge stripe for a ticket card or row. Empty when there is no clock. */
export function slaEdgeClass(state: SlaState): string {
  return state ? `border-l-4 ${STYLE[state].edge}` : 'border-l-4 border-l-transparent';
}

export function SlaBadge({
  state,
  percent,
  targetMin,
  compact = false,
}: {
  state: SlaState;
  percent?: number | null;
  targetMin?: number | null;
  compact?: boolean;
}) {
  if (!state) return null;
  const { chip, icon: Icon, label } = STYLE[state];

  const pct = percent === null || percent === undefined ? null : Math.round(percent * 100);

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium ${chip}`}
      title={
        targetMin
          ? `Target ${Math.round(targetMin / 60)}h${pct !== null ? ` · ${pct}% used` : ''}`
          : undefined
      }
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {compact && pct !== null ? `${pct}%` : label}
    </span>
  );
}

/**
 * How much of the target has been spent.
 *
 * Capped at 100% width so a badly breached ticket does not draw a bar across
 * the next column — the number beside it still says 240%.
 */
export function SlaBar({ state, percent }: { state: SlaState; percent?: number | null }) {
  if (!state || percent === null || percent === undefined) return null;
  const width = Math.min(100, Math.round(percent * 100));

  return (
    <div className="flex items-center gap-2">
      <div
        className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={Math.round(percent * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="SLA target used"
      >
        <div className={`h-full rounded-full ${STYLE[state].bar}`} style={{ width: `${width}%` }} />
      </div>
      <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
        {Math.round(percent * 100)}%
      </span>
    </div>
  );
}
